import React, {useState, useEffect, useRef} from 'react';
import styles from './HomePage.module.css';
import logo from './assets/pokerly-logo.svg';
import HomeBg from './assets/HomeBg.svg';
import {useNavigate} from 'react-router-dom';
import {createBucketRoom, createRoom} from './roomActions';
import {db} from './firebase';
import DynamicDescription from './components/DynamicDescription/DynamicDescription';
import {Helmet} from "react-helmet-async";
import i18n from "./i18n";
import {useTranslation} from 'react-i18next';


// внутри компонента HomePage
<Helmet>
    <title>{i18n.language === "ru" ? "Pokerly — онлайн покер-планирование" : "Pokerly — agile estimation planning poker"}</title>
    <meta
        name="description"
        content={
            i18n.language === "ru"
                ? "Pokerly — сервис для оценки задач по методу Planning Poker. Быстро, понятно и бесплатно."
                : "Pokerly — a tool for agile story point estimation using planning poker. Simple, real-time, and free."
        }
    />
    <meta property="og:type" content="website"/>
    <meta property="og:url" content={window.location.href}/>
    <meta property="og:title" content="Pokerly"/>
    <meta
        property="og:description"
        content={
            i18n.language === "ru"
                ? "Создавайте комнаты и оценивайте задачи в команде по методу Planning Poker."
                : "Create rooms and estimate tasks with your team using Planning Poker."
        }
    />
    <meta
        property="og:image"
        content={`https://${window.location.hostname}/og-image.png`}
    />

    <meta name="twitter:card" content="summary_large_image"/>
    <meta name="twitter:title" content="Pokerly"/>
    <meta
        name="twitter:description"
        content={
            i18n.language === "ru"
                ? "Создавайте комнаты и оценивайте задачи в команде по методу Planning Poker."
                : "Create rooms and estimate tasks with your team using Planning Poker."
        }
    />
    <meta
        name="twitter:image"
        content={`https://${window.location.hostname}/og-image.png`}
    />
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "WebSite",
                    "name": "Pokerly",
                    "url": `https://${window.location.hostname}`,
                    "description":
                        i18n.language === "ru"
                            ? "Pokerly — это онлайн-инструмент для оценки задач в Agile с помощью покерных карт. Создавайте комнаты, приглашайте команду и планируйте спринты легко."
                            : "Pokerly is an online tool for agile estimation with Fibonacci cards. Create a room, invite teammates, and plan your sprints easily.",
                    "inLanguage": i18n.language,
                    "publisher": {
                        "@type": "Organization",
                        "name": "Pokerly",
                        "url": `https://${window.location.hostname}`,
                        "logo": {
                            "@type": "ImageObject",
                            "url": `https://${window.location.hostname}/logo512.png`,
                            "width": 512,
                            "height": 512
                        }
                    },
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": `https://${window.location.hostname}/?q={search_term_string}`,
                        "query-input": "required name=search_term_string"
                    }
                },
                {
                    "@type": "Product",
                    "name": "Pokerly",
                    "url": `https://${window.location.hostname}`,
                    "description":
                        i18n.language === "ru"
                            ? "Pokerly — инструмент для оценки задач по Agile с помощью планировочного покера. Создайте комнату, пригласите команду и оцените задачи вместе."
                            : "Pokerly is an agile estimation tool using planning poker cards. Create a room, invite your team, and estimate story points collaboratively.",
                    "inLanguage": i18n.language,
                    "image": `https://${window.location.hostname}/og-image.png`,
                    "brand": {
                        "@type": "Brand",
                        "name": "Pokerly"
                    },
                    "offers": {
                        "@type": "Offer",
                        "price": "0.00",
                        "priceCurrency": i18n.language === "ru" ? "RUB" : "USD",
                        "availability": "https://schema.org/InStock"
                    },
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": "5.0",
                        "reviewCount": "15"
                    }
                }
            ]
        })}
    </script>
</Helmet>


import {
    collection,
    getDocs,
    addDoc,
    doc,
    setDoc
} from 'firebase/firestore';
import RoomHistory from 'components/RoomHistory/RoomHistory';

const HomePage: React.FC = () => {
    const [roomName, setRoomName] = useState('');
    const [userName, setUserName] = useState('');
    const [selectedScale, setSelectedScale] = useState('');
    const [scales, setScales] = useState<string[]>([]);
    const [isFacilitated, setIsFacilitated] = useState(true);
    const [userId, setUserId] = useState<string>('');
    const [joinRoomId, setJoinRoomId] = useState('');
    const [joinUserName, setJoinUserName] = useState('');
    const [roomType, setRoomType] = useState("poker");
    const [maxDistance, setMaxDistance] = useState<number>(100);
    const [maxDotSpacing, setMaxDotSpacing] = useState<number>(20);
    const [maxMagnet, setMaxMagnet] = useState<number>(900);
    const [blockHeight, setBlockHeight] = useState<string>('100%');
    const {t} = useTranslation();

    const navigate = useNavigate();

    // Initialize userId
    useEffect(() => {
        let storedUserId = localStorage.getItem("userId");
        if (!storedUserId) {
            storedUserId = Math.random().toString(36).substring(2, 10);
            localStorage.setItem("userId", storedUserId);
        }
        setUserId(storedUserId);
    }, []);

    useEffect(() => {
        const fetchScales = async () => {
            const snapshot = await getDocs(collection(db, 'scales'));
            const loadedScales = snapshot.docs.map(doc => doc.id);

            if (loadedScales.length === 0) {
                setScales([
                    "Fibonacci sequence",
                    "Fibonacci sequence (modified)",
                    "T-shirt sizing",
                    "Custom"
                ]);
            } else {
                setScales(loadedScales);
            }
        };
        fetchScales();
    }, []);
    useEffect(() => {
        if (scales.length > 0 && !selectedScale) {
            setSelectedScale(scales[0]);
        }
    }, [scales]);
    const mapScaleToKey = (label: string): string => {
        switch (label) {
            case "Fibonacci sequence":
                return "fibonacci";
            case "Fibonacci sequence (modified)":
                return "fibonacciModified";
            case "T-shirt sizing":
                return "tshirt";
            case "Custom":
                return "custom";
            default:
                return "fibonacci";
        }
    };
    console.log("Тип оценки:", selectedScale, "→", mapScaleToKey(selectedScale));

    const handleCreateRoom = async () => {
        if (!roomName || !userName || !selectedScale) return;

        const roomId = Math.random().toString(36).substring(2, 10);

        if (roomType === "bucket") {
            await createBucketRoom(
                roomId,
                userId,
                userName,
                roomName,
                mapScaleToKey(selectedScale)
            );

            localStorage.setItem("userName", userName);
            navigate(`/bucket/${roomId}`);
        } else {
            await createRoom(
                roomId,
                userId,
                userName,
                roomName,
                mapScaleToKey(selectedScale),
                isFacilitated ? "withFacilitator" : "withoutFacilitator"
            );

            localStorage.setItem("userName", userName);
            navigate(`/room/${roomId}`);
        }
    };

    const handleJoinRoom = () => {
        if (!joinRoomId || !joinUserName) return;

        localStorage.setItem('userName', joinUserName);
        navigate(`/room/${joinRoomId}`);
    };

    const blockRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        setTimeout(() => {
            if (blockRef.current) {
                const height = blockRef.current.offsetHeight;
                setBlockHeight(String(height));
            }
        }, 700);
    }, []);

    return (
        <div className={styles.SectionHome}>
            <div className={styles.HomeBg}>
                <img src={HomeBg}/>
            </div>
            <div className={styles.NewContainer}>
                <div className={styles.SectionHomeWrp}>
                    <div className={styles.SectionHomeBg} style={{height: `${blockHeight}px`}}>
                    </div>
                    <div className={styles.SectionHomeBg} style={{height: `${blockHeight}px`}}>
                    </div>
                    <div className={styles.SectionHomeContent} ref={blockRef}>
                        <img src={logo} alt="Pokerly Logo" className={styles.SectionHomeLogo}/>
                        <div className={styles.SectionHomeDescription}>
                            <DynamicDescription lang={i18n.language === "ru" ? "ru" : "en"}/>
                        </div>
                        <div className={styles.FormWrp}>
                            <form
                                className={styles.FormReg}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleCreateRoom();
                                }}
                            >
                                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                    <div className={styles.SectionHomeH2}>{t("createRoomTitle")}</div>
                                    <div className={styles.FormRegRadioGroup}>
                                        <label className={styles.radio}>
                                            <input
                                                type="radio"
                                                name="roomType"
                                                value="poker"
                                                checked={roomType === "poker"}
                                                onChange={(e) => setRoomType(e.target.value)}
                                            />
                                            <span>{t("roomTypePoker")}</span>
                                        </label>
                                        <label className={styles.radio}>
                                            <input
                                                type="radio"
                                                name="roomType"
                                                value="bucket"
                                                checked={roomType === "bucket"}
                                                onChange={(e) => setRoomType(e.target.value)}
                                            />
                                            <span>{t("roomTypeBucket")}</span>
                                        </label>
                                    </div>
                                </div>
                                <div className={styles.FormRegInputGroup}>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        placeholder={t("roomName")}
                                        value={roomName}
                                        onChange={(e) => setRoomName(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        className={styles.input}
                                        placeholder={t("yourName")}
                                        value={userName}
                                        onChange={(e) => setUserName(e.target.value)}
                                    />
                                </div>
                                <div className={styles.FormRegSelectGroup}>
                                    <select
                                        className={styles.input}
                                        value={selectedScale}
                                        onChange={(e) => setSelectedScale(e.target.value)}
                                    >
                                        <option value="" disabled hidden>{t("estimationType")}</option>
                                        {scales.map((scale) => (
                                            <option key={scale} value={scale}>{scale}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.FormRegCheckbox}>
                                    <label className={styles.radio}>
                                        <input
                                            type="checkbox"
                                            checked={roomType === "bucket" ? true : isFacilitated}
                                            disabled={roomType === "bucket"}
                                            onChange={({target}) => setIsFacilitated(target.checked)}
                                        />
                                        <span>{t("withFacilitator")}</span>
                                    </label>
                                </div>
                                <button className={styles.createButton}>
                                    {t("createRoom")}
                                </button>
                            </form>
                            <form
                                className={styles.FormAuth}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleJoinRoom();
                                }}
                            >
                                <div className={styles.SectionHomeH2}>{t("joinRoom")}</div>
                                <div className={styles.FormAuthWrp}>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        placeholder={t("roomId")}
                                        value={joinRoomId}
                                        onChange={(e) => setJoinRoomId(e.target.value)}
                                    />
                                    <input
                                        type="text"
                                        className={styles.input}
                                        placeholder={t("yourName")}
                                        value={joinUserName}
                                        onChange={(e) => setJoinUserName(e.target.value)}
                                    />
                                    <button className={`${styles.joinButton} ${styles.createButton}`}>
                                        {t("joinRoomButton")}
                                    </button>
                                </div>
                            </form>
                            {userId && <RoomHistory userId={userId}/>}
                        </div>
                        <div className={styles.copyright}>2025 © Made with ❤️ from us. All rights reserved.</div>
                    </div>
                </div>
            </div>
        </div>
    )
        ;
};

export default HomePage;
